/**
 * An order's payment state on the patient's order page (ORD-22), as the app shows it:
 * - while the pharmacy revises the published price (`price_revision`), paying is paused — the API
 *   refuses with PRICE_BEING_REVISED — until the new price is published or the edit window ends;
 * - the latest attempt's failure reason, in words.
 */
import { useEffect, useState } from "react";

import type { OrderPayment, PriceRevision } from "../api/orders";

// The shapes live with the order API; re-exported for the screens that import them from here.
export type { OrderPayment, PriceRevision };

/** How often a paused order is fetched again, to pick up the new price. */
export const PAUSED_REFRESH_MS = 10_000;

/** Seconds until the pause ends, ticking from the server's value; null when not paused. */
export function usePauseCountdown(revision: PriceRevision | null | undefined): number | null {
  const seconds = revision ? revision.seconds_left : null;
  const key = revision ? revision.expires_at : null;
  const [left, setLeft] = useState<number | null>(seconds);
  useEffect(() => {
    if (seconds === null) {
      setLeft(null);
      return;
    }
    const started = Date.now();
    setLeft(seconds);
    const timer = setInterval(() => {
      const next = Math.max(0, seconds - Math.floor((Date.now() - started) / 1000));
      setLeft(next);
      if (next === 0) clearInterval(timer);
    }, 1000);
    return () => clearInterval(timer);
  }, [seconds, key]);
  return left;
}

/** 272 → "4:32". */
export function formatCountdown(totalSeconds: number): string {
  const s = Math.max(0, Math.floor(totalSeconds));
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`;
}

/** The latest attempt's failure in words, without the machine code the API appends in brackets. */
export function failureReasonText(payment?: OrderPayment | null): string {
  const raw = (payment?.failure_reason ?? "").trim();
  const generic = "Your last payment attempt didn’t go through.";
  if (!raw) return generic;
  const text = raw.replace(/\s*\([a-z0-9_]+\)\s*$/i, "").trim();
  if (!text || /^payment failed$/i.test(text)) return generic;
  return text.endsWith(".") ? text : `${text}.`;
}
